import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import { route } from 'preact-router'
import { LoadingSpinner } from './LoadingSpinner.js'
import { api } from '../services/api.js'

export function Sources({ sources, setSources, selectedSource, setSelectedSource }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (sources.length === 0) {
      loadSources()
    }
  }, [])

  const loadSources = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/sources')
      setSources(response.data.sources)
    } catch (error) {
      console.error('Error loading sources:', error)
      setError('Eroare la încărcarea surselor.')
    } finally {
      setLoading(false)
    }
  }

  const toggleSource = async (sourceId) => {
    try {
      await api.post(`/sources/${sourceId}/toggle`)
      // Refresh sources to get updated status
      loadSources()
    } catch (error) {
      console.error('Error toggling source:', error)
      setError('Eroare la actualizarea sursei.')
    }
  }

  const selectSource = (source) => {
    setSelectedSource(source)
    route('/')
  }

  const clearSelection = () => {
    setSelectedSource(null)
    route('/')
  }

  if (loading) {
    return html`
      <div class="flex justify-center items-center py-12">
        <${LoadingSpinner} />
      </div>
    `
  }

  if (error) {
    return html`
      <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <p class="text-red-600 dark:text-red-400 mb-4">${error}</p>
        <button 
          onClick=${loadSources}
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Încercați din nou
        </button>
      </div>
    `
  }

  return html`
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
            Surse de știri
          </h2>
          <p class="text-gray-600 dark:text-gray-400">
            ${sources.length} surse configurate
          </p>
        </div>

        <div class="flex gap-3">
          ${selectedSource && html`
            <button
              onClick=${clearSelection}
              class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Arată toate
            </button>
          `}
          
          <button
            onClick=${loadSources}
            class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            disabled=${loading}
          >
            <span class=${loading ? 'animate-spin' : ''}>🔄</span>
            Actualizează
          </button>
        </div>
      </div>

      <!-- Sources Grid -->
      <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        ${sources.map(source => {
          const isSelected = selectedSource?.id === source.id
          const isActive = source.is_active
          
          return html`
            <div 
              key=${source.id}
              class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border 
                     ${isSelected ? 'border-primary-500 ring-2 ring-primary-500 ring-opacity-20' : 'border-gray-200 dark:border-gray-700'}
                     p-6 transition-all duration-200 hover:shadow-md"
            >
              <!-- Source Header -->
              <div class="flex items-start justify-between mb-4">
                <div class="flex-1">
                  <h3 class="font-semibold text-gray-900 dark:text-white mb-1">
                    ${source.name}
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 truncate" title=${source.url}>
                    ${source.url}
                  </p>
                </div>
                
                <!-- Status Indicator -->
                <div class="flex items-center gap-2">
                  <div class="${isActive ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'} 
                             px-2 py-1 rounded-full text-xs font-medium">
                    ${isActive ? 'Activ' : 'Inactiv'}
                  </div>
                </div>
              </div>

              <!-- Source Info -->
              <div class="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <div class="flex justify-between">
                  <span>Tip:</span>
                  <span class="font-medium capitalize">${source.source_type}</span>
                </div>
                <div class="flex justify-between">
                  <span>Frecvență:</span>
                  <span class="font-medium">${source.frequency_minutes} min</span>
                </div>
                <div class="flex justify-between">
                  <span>Articole:</span>
                  <span class="font-medium">${source.article_count || 0}</span>
                </div>
                ${source.last_scraped && html`
                  <div class="flex justify-between">
                    <span>Ultima verificare:</span>
                    <span class="font-medium">${dayjs(source.last_scraped).fromNow()}</span>
                  </div>
                `}
              </div>

              <!-- Actions -->
              <div class="flex gap-2">
                <button
                  onClick=${() => selectSource(source)}
                  class="${isSelected ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'} 
                         px-3 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 hover:text-white 
                         transition-colors flex-1"
                >
                  ${isSelected ? 'Selectat' : 'Selectează'}
                </button>
                
                <button
                  onClick=${() => toggleSource(source.id)}
                  class="${isActive ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400' : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'} 
                         px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  title=${isActive ? 'Dezactivează sursa' : 'Activează sursa'}
                >
                  ${isActive ? '⏸️' : '▶️'}
                </button>
              </div>
            </div>
          `
        })}
      </div>

      ${sources.length === 0 && html`
        <div class="text-center py-12">
          <p class="text-gray-500 dark:text-gray-400 text-lg">
            Nu au fost găsite surse configurate.
          </p>
        </div>
      `}
    </div>
  `
}